import { useCallback, useEffect, useRef, useState } from 'react'
import { rigBoardSource, subscribeRigBoard } from '../ogs/rig/rigBoardSource.js'
import { faceGraphState, notifyFaceGraphInsert } from '../lib/faceGraphState.js'
import { rigControlState } from '../lib/rigControlState.js'
import { ogsGpuFlags } from '../ogs/inference/ogsGpuFlags'
import {
  prettyRigLabel, poseRow, poseCount, boardLimits, applyCoupling,
  macroSubset, buildMacro, deriveEyeRefs, buildMouthController,
} from '../lib/rigBoard.js'

/**
 * The DNA's RAW control layer, for the rig vector currently on the board.
 *
 * TWO SENSES OF "RAW" MEET HERE and only one of them is this section's. The
 * board's raw rig is THE BUNDLE'S OWN control vector, as opposed to the ~third
 * of it ARKit-52 can reach — and that vector is written in RigLogic's *GUI*
 * layer (`CTRL_C_jaw.ty`, 174 of them). RigLogic's RAW layer is a disjoint
 * namespace of 263 (`CTRL_expressions.jawOpen`), and it is what a
 * SOMA-X-solved model is conditioned on. This shows the correspondence.
 *
 * WHAT IT IS NOT. The mapping runs through RigLogic's conditional tables, which
 * invites the assumption that one widget fans out across several expressions.
 * Measured on a real head.dna, it does not: a single edit lights exactly one
 * raw control or none, and a full pose is exactly the sum of its edits
 * (tests/orl/orlDeform.test.mjs). So this is a correspondence view — which raw
 * control a slider IS, in the namespace the model speaks — and it is captioned
 * that way rather than promising a view of tables combining.
 *
 * EDITABLE, for the controls that have a GUI source to route back through.
 * The 20 that do not are shown read-only when active — a slider that moves and
 * changes nothing reads as a broken rig, not as a derived control.
 */
function RawLayerBoard({ rawLayer, rig, dim, onApply }) {
  const [open, setOpen] = useState(false)
  // Converted only while the section is open. It is a wasm round trip per edit
  // and it CLOBBERS the rig's pose (see orlRig.js guiToRaw), so a closed panel
  // must not be paying for it on every slider move.
  const raw = useDerived(
    () => (open && rawLayer && rig ? rawLayer.guiToRaw(rig) : null),
    [open, rawLayer, rig],
  )
  // Which raw controls an edit can reach. Lazy — the inverse table is built on
  // the first call, so a session that never opens this pays nothing.
  const drivable = useDerived(
    () => (open && rawLayer ? rawLayer.drivable() : null),
    [open, rawLayer],
  )

  if (!rawLayer) {
    return (
      <div style={noteStyle} data-testid="raw-layer-unavailable">
        RigLogic&rsquo;s raw control layer is not available for this character. It
        exists only when the exact MetaHuman rig is driving the head
        (<code>?rig=orl</code>); the distilled <code>rig2mesh</code> path has no
        DNA behind it, so there is no second control layer to show.
      </div>
    )
  }

  function onRawSlider(i, v) {
    if (!raw) return
    const next = Float32Array.from(raw)
    next[i] = v
    // LOSSY BY CONSTRUCTION, and said so in the note above: this re-derives the
    // whole control vector through rawToGui's +-1 probe lookup, which has no
    // entry for a raw control sourced non-linearly. Those come back at zero, so
    // an edit to one slider can drop another's contribution. Surfaced rather
    // than fixed — carrying the unrepresentable entries would mean tracking the
    // bundle vector alongside the raw one, which is a bigger change than this
    // readout warrants (see orlRig.js rawToGui).
    // Back to the BUNDLE's control vector — the one the decoders take. Driving
    // RigLogic's raw controls directly would move the mesh while the appearance
    // decoder still read the unchanged bundle vector.
    const controls = rawLayer.rawToControls(next)
    if (controls) onApply(controls)
  }

  const nActive = raw ? raw.reduce((n, v) => n + (Math.abs(v) > 1e-4 ? 1 : 0), 0) : 0
  const nDrivable = drivable ? drivable.filter(Boolean).length : 0

  return (
    <div style={{ marginBottom: 10 }} data-testid="raw-layer">
      <button
        type="button" data-testid="raw-layer-toggle" onClick={() => setOpen(o => !o)}
        style={{ ...btnStyle, width: '100%', textAlign: 'left' }}
      >
        {open ? '▾' : '▸'} RigLogic RAW ({rawLayer.names.length}){' '}
        <span style={{ color: 'var(--m-fg-3)' }}>
          {open ? `— ${nActive} active` : `— drive the model's own control namespace`}
        </span>
      </button>
      {open && (
        <div data-testid="raw-layer-body">
          <div style={{ ...noteStyle, marginTop: 6 }}>
            The board above drives this bundle&rsquo;s {dim} controls, in
            RigLogic&rsquo;s GUI layer. These are the {rawLayer.names.length} RAW
            activations they map to &mdash; the namespace a solved model is
            conditioned on. Editing one converts back through the inverse, so the
            mesh and the appearance decoder stay on one vector. The inverse is
            APPROXIMATE and an edit re-derives the WHOLE vector, so a control
            whose transfer is non-linear in value (the sticky-lip pair, a step
            rather than a ramp) does not survive the trip and comes back at
            zero &mdash; editing anything here can drop such a contribution the
            pose already had.
            {drivable && nDrivable < rawLayer.names.length && (
              <> The other {rawLayer.names.length - nDrivable} are DERIVED, not
              driven &mdash; RigLogic&rsquo;s own interpolation stages and the
              head/neck pose quaternions &mdash; so they are shown read-only
              rather than as sliders that would move and change nothing.</>
            )}
          </div>
          {!raw && <div style={noteStyle}>Move a control to populate this.</div>}
          {raw && rawLayer.names.map((name, i) => {
            const canDrive = drivable ? drivable[i] : false
            const v = raw[i]
            // Read-only entries are worth showing only when they are DOING
            // something; 20 permanently-zero rows would just be noise.
            if (!canDrive && Math.abs(v) <= 1e-4) return null
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
                <label
                  title={name}
                  style={{
                    flex: 'none', width: 150, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', color: canDrive ? 'var(--m-fg-2)' : 'var(--m-fg-3)',
                  }}
                >
                  {name.replace(/^CTRL_expressions\./, '')}
                </label>
                {canDrive ? (
                  <input
                    type="range" min="0" max="1" step="0.01" value={v}
                    data-testid={`raw-layer-slider-${i}`}
                    onChange={e => onRawSlider(i, parseFloat(e.target.value))}
                    className="aos-slider"
                    style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fillPct(v, 0, 1) }}
                  />
                ) : (
                  <span style={{ flex: 1, minWidth: 0, color: 'var(--m-fg-3)' }}>derived</span>
                )}
                <span style={{ flex: 'none', width: 38, textAlign: 'right', color: 'var(--m-fg-3)' }}>
                  {v.toFixed(2)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * RawRigBoard — the bundle's OWN MetaHuman control board (ENG-946).
 *
 * Every control the decoders were trained on, addressed directly, instead of
 * the ~third of them ARKit-52 can reach. This is the board the TexAvatars
 * research viewer exposes; the rules it needs to stay on-manifold (trained
 * limits, coupling, configuration macros) live in lib/rigBoard.js.
 *
 * Splat characters only, and the board is entirely bundle-driven: names,
 * range, pose table and macros all come from the loaded /ogs bundle, so a
 * different character simply yields a different board.
 */
export default function RawRigBoard() {
  const [board, setBoard] = useState(() => snapshot())
  const [rig, setRig] = useState(null)
  const [pose, setPose] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [fps, setFps] = useState(30)
  const [macroT, setMacroT] = useState({ mouth: 0, eyesL: 0, eyesR: 0, mouthCtl: 0 })
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(null)
  const rigRef = useRef(null)
  rigRef.current = rig
  // The base-conditioned mouth controller, built against the rig the user
  // started from. Held in a ref and invalidated by every OTHER edit path, which
  // is what makes a drag idempotent: rebuilding from the live rig each frame
  // would walk the trained references along with the controller's own output.
  const mouthCtlRef = useRef(null)

  useEffect(() => subscribeRigBoard(() => setBoard(snapshot())), [])

  // Pull the trained pose table on first open — it is what the limits, the
  // macros and the pose player are all derived from.
  useEffect(() => {
    let cancelled = false
    if (!board.ready || board.presets) return
    if (!rigBoardSource.loadPresets) return
    rigBoardSource.loadPresets()
      .then(() => { if (!cancelled) { setBoard(snapshot()); setError(null) } })
      .catch(e => { if (!cancelled) setError(e?.message ?? String(e)) })
    return () => { cancelled = true }
  }, [board.ready, board.presets])

  const { names, dim, range, presets, coupling, macros, mouthController, rawLayer } = board

  // Derived board rules — recomputed only when the bundle changes.
  // The SAME clamp the live ARKit path uses (?rigclamp / ?rigclamppad): a board
  // that kept clamping to the trained box while the flag relaxed the live path
  // would make the two UIs disagree about the same pose, which is the split the
  // shared `trainedLimits` exists to prevent. Null → the sliders span the
  // declared rig_range, the widest a widget can offer.
  const clampFlags = useDerived(() => ogsGpuFlags(), [])
  const limits = useDerived(
    () => boardLimits({ presets, dim, range, clamp: clampFlags.rigClamp, pad: clampFlags.rigClampPad }),
    [presets, dim, range, clampFlags])
  const nPoses = useDerived(() => presets ? poseCount(presets, dim) : 0, [presets, dim])
  const mouthMacro = useDerived(() => {
    if (!presets || !names || !macros) return null
    const o = macros.mouth_open_pose, c = macros.mouth_closed_pose
    if (o === undefined || c === undefined) return null
    return buildMacro({
      subset: macroSubset(names, 'mouth'),
      openRef: poseRow(presets, dim, o),
      closedRef: poseRow(presets, dim, c),
    })
  }, [presets, names, macros, dim])
  // Whether the controller can actually be BUILT against the current base — not
  // merely whether the bundle declares a block. buildMouthController also returns
  // null when the pools don't resolve in range, and gating on the block alone
  // rendered a row whose every drag returned early: a slider that moves and does
  // nothing, which is the fake slider this is supposed to prevent.
  // PER SIDE. One slider for both eyes cannot express a wink, and closing the
  // left eye used to drag the right shut with it.
  const eyeMacros = useDerived(() => {
    if (!presets || !names) return null
    const refs = deriveEyeRefs(names, presets, dim)
    if (!refs) return null
    const openRef = poseRow(presets, dim, refs.openRow)
    const closedRef = poseRow(presets, dim, refs.closedRow)
    const side = (s) => {
      const subset = macroSubset(names, 'eyes', s)
      return subset.length ? buildMacro({ subset, openRef, closedRef }) : null
    }
    return { L: side('L'), R: side('R') }
  }, [presets, names, dim])

  // Arm raw mode while this board is mounted: the ARKit path stands down so
  // there is one writer on the rig vector.
  useEffect(() => {
    rigControlState.rawMode = true
    return () => { rigControlState.rawMode = false }
  }, [])

  // Seed from the first trained pose, so the board opens on a real face rather
  // than an all-zeros configuration the decoders never saw.
  useEffect(() => {
    if (rig || !presets || !dim) return
    applyRig(poseRow(presets, dim, 0), { syncMacros: true })
  }, [presets, dim, rig]) // eslint-disable-line react-hooks/exhaustive-deps

  const push = useCallback((next) => {
    rigBoardSource.setRig?.(next)
  }, [])

  function applyRig(next, { syncMacros = false } = {}) {
    setRig(next)
    push(next)
    // This rig came from somewhere other than the mouth controller (a pose
    // recall, a slider, the initial seed), so it is the new BASE its references
    // must be picked against.
    mouthCtlRef.current = null
    if (syncMacros) {
      setMacroT({
        mouth: mouthMacro ? mouthMacro.valueOf(next) : 0,
        eyesL: eyeMacros?.L ? eyeMacros.L.valueOf(next) : 0,
        eyesR: eyeMacros?.R ? eyeMacros.R.valueOf(next) : 0,
        mouthCtl: mouthCtl(next)?.valueOf(next) ?? 0,
      })
    }
  }

  /** The controller for the CURRENT base, built once and cached until some other
   *  edit path replaces the base. Null when the bundle ships no
   *  `mouth_controller` block or the pose table isn't loaded — the caller then
   *  simply has no such slider, and the global macro remains. */
  function mouthCtl(base) {
    // Wrapped so a NULL result is cached too: buildMouthController scans both
    // reference pools, and an unusable spec would otherwise re-scan them on
    // every applyRig (i.e. every slider-drag event).
    if (!mouthCtlRef.current) {
      mouthCtlRef.current = {
        ctl: buildMouthController({ spec: mouthController, pose: base, presets, dim }),
      }
    }
    return mouthCtlRef.current.ctl
  }

  function onSlider(index, value) {
    const cur = rigRef.current
    if (!cur) return
    const next = Float32Array.from(cur)
    next[index] = value
    // Single-control drags get their trained companions moved with them.
    if (coupling) applyCoupling(next, cur, coupling, limits, range)
    applyRig(next, { syncMacros: true })
  }

  function onMacro(kind, t) {
    const macro = kind === 'mouth' ? mouthMacro : kind === 'eyesL' ? eyeMacros?.L : eyeMacros?.R
    const cur = rigRef.current
    if (!macro || !cur) return
    const t0 = macro.valueOf(cur)
    // A macro moves the rig off the base the controller resolved against.
    mouthCtlRef.current = null
    setMacroT(prev => ({ ...prev, [kind]: t }))
    setRig(() => { const out = macro.apply(cur, t, t0); push(out); return out })
  }

  /** The base-conditioned mouth controller. Unlike the macro this does NOT read
   *  `cur` to pick its references — they belong to the base — so dragging back
   *  and forth returns the same rig for the same t. */
  function onMouthController(_kind, t) {
    const cur = rigRef.current
    if (!cur) return
    const ctl = mouthCtl(cur)
    if (!ctl) return
    setMacroT(prev => ({ ...prev, mouthCtl: t }))
    setRig(() => { const out = ctl.apply(cur, t); push(out); return out })
  }

  function gotoPose(i) {
    if (!presets) return
    setPose(i)
    applyRig(poseRow(presets, dim, i), { syncMacros: true })
  }

  // Trained-frame playback at the chosen fps.
  useEffect(() => {
    if (!playing || !nPoses) return
    const id = setInterval(() => {
      setPose(prev => {
        const next = (prev + 1) % nPoses
        if (presets) applyRig(poseRow(presets, dim, next), { syncMacros: false })
        return next
      })
    }, Math.max(16, 1000 / fps))
    return () => clearInterval(id)
  }, [playing, fps, nPoses, presets, dim]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Save as pose" — the whole point of the board is finding a look; this is
  // how that look leaves the panel. The configuration is written INTO a rigPose
  // node (data.pose), so the blueprint is self-contained: no server round-trip,
  // and no preset index that could drift when the bundle is re-exported.
  function saveAsPose() {
    const cur = rigRef.current
    if (!cur) return
    const id = `rig_pose_${faceGraphState.nodes.length}_${Math.round(cur[0] * 1000)}`
    const existing = faceGraphState.nodes.find(n => n.id === id)
    if (existing) { setSaved(`already saved as ${id}`); return }
    // Offset each new node so successive saves don't stack invisibly.
    const n = faceGraphState.nodes.filter(x => x.type === 'rigPose').length
    const node = {
      id,
      type: 'rigPose',
      position: { x: 120, y: 120 + n * 130 },
      data: { pose: Array.from(cur), poseIndex: pose },
    }
    faceGraphState.nodes.push(node)
    notifyFaceGraphInsert(node)
    setSaved(`saved — a Rig Pose node is in the Face graph (${id})`)
  }

  if (!board.ready) {
    return (
      <div data-testid="raw-rig-unavailable" style={noteStyle}>
        This character has no OGS control rig, so there is no raw board to show.
        Raw mode needs a gaussian-splat character whose bundle ships
        <code> rig_names.json</code> — switch to ARKit mode for a GLB character.
      </div>
    )
  }

  return (
    <div data-testid="raw-rig-board">
      {error && (
        <div data-testid="raw-rig-error" style={{ ...noteStyle, color: 'var(--m-warn)' }}>
          Trained poses unavailable ({error}). Sliders still drive the rig; presets,
          macros and per-control limits need that table.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <button type="button" data-testid="raw-rig-save-pose" onClick={saveAsPose} style={btnStyle}>
          Save as pose
        </button>
        <span style={{ color: 'var(--m-fg-3)', flex: 1, minWidth: 0 }}>
          adds a Rig Pose node to the Face graph
        </span>
      </div>
      {saved && (
        <div data-testid="raw-rig-saved" style={{ ...noteStyle, background: 'color-mix(in srgb, var(--m-accent) 14%, transparent)' }}>
          {saved}
        </div>
      )}

      <RawLayerBoard rawLayer={rawLayer} rig={rig} dim={dim} onApply={r => applyRig(r, { syncMacros: true })} />

      {/* Trained-frame player */}
      {nPoses > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={sectionLabel}>Presets (trained frames)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
            <label style={{ flex: 'none', width: 96, color: 'var(--m-fg-2)' }}>
              Pose {pose} / {nPoses - 1}
            </label>
            <input
              type="range" min="0" max={nPoses - 1} step="1" value={pose}
              data-testid="raw-rig-pose"
              onChange={e => gotoPose(parseInt(e.target.value, 10))}
              className="aos-slider"
              style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fillPct(pose, 0, nPoses - 1) }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button" data-testid="raw-rig-play"
              onClick={() => setPlaying(p => !p)}
              style={btnStyle}
            >{playing ? 'Pause' : 'Play'}</button>
            <span style={{ color: 'var(--m-fg-3)' }}>Speed</span>
            <input
              type="range" min="1" max="60" step="1" value={fps}
              onChange={e => setFps(parseInt(e.target.value, 10))}
              className="aos-slider"
              style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fillPct(fps, 1, 60) }}
            />
            <span style={{ width: 42, textAlign: 'right', color: 'var(--m-fg-3)' }}>{fps} fps</span>
          </div>
        </div>
      )}

      <div style={sectionLabel}>Rig parameters ({dim})</div>

      {/* Macros first — they drive whole configurations, so they belong above
          the individual controls they move. */}
      {/* Both mouth controls ship when the bundle supports both, so the two stay
          A/B-comparable: the macro nudges companions along one GLOBAL
          closed→open trajectory (keeping this pose's own mouth residuals), the
          controller REPLACES the block with an interpolation between the trained
          references nearest this pose. */}
      {rig && mouthCtl(rig) && (
        <MacroRow
          id="mouthCtl"
          label="Mouth open (trained)"
          value={macroT.mouthCtl}
          onChange={onMouthController}
        />
      )}
      {mouthMacro && (
        <MacroRow id="mouth" label="Mouth open (macro)" value={macroT.mouth} onChange={onMacro} />
      )}
      {eyeMacros?.L && (
        <MacroRow id="eyesL" label="Eye close L (macro)" value={macroT.eyesL} onChange={onMacro} />
      )}
      {eyeMacros?.R && (
        <MacroRow id="eyesR" label="Eye close R (macro)" value={macroT.eyesR} onChange={onMacro} />
      )}

      {names.map((name, i) => {
        const lim = limits?.[i] ?? range
        const v = rig ? rig[i] : 0
        return (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
            <label title={name} style={ctrlLabel}>{prettyRigLabel(name)}</label>
            <input
              type="range"
              min={lim[0]} max={lim[1]} step="0.01"
              value={v}
              data-testid={`raw-rig-slider-${i}`}
              onChange={e => onSlider(i, parseFloat(e.target.value))}
              className="aos-slider"
              style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fillPct(v, lim[0], lim[1]) }}
            />
            <span style={{
              flex: 'none', width: 30, textAlign: 'right',
              color: v > 0.001 ? 'var(--m-accent)' : 'var(--m-fg-3)',
            }}>{v.toFixed(2)}</span>
          </div>
        )
      })}
    </div>
  )
}

function MacroRow({ id, label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
      <label style={{ ...ctrlLabel, color: 'var(--m-fg)' }}>{label}</label>
      <input
        type="range" min="0" max="1" step="0.01" value={value}
        data-testid={`raw-rig-macro-${id}`}
        onChange={e => onChange(id, parseFloat(e.target.value))}
        className="aos-slider"
        style={{ flex: 1, minWidth: 0, '--aos-slider-fill': fillPct(value, 0, 1) }}
      />
      <span style={{ flex: 'none', width: 30, textAlign: 'right', color: 'var(--m-accent)' }}>
        {value.toFixed(2)}
      </span>
    </div>
  )
}

function snapshot() {
  const s = rigBoardSource
  return {
    ready: !!(s.names && s.dim && s.setRig),
    subject: s.subject,
    names: s.names,
    dim: s.dim,
    range: s.range,
    presets: s.presets,
    coupling: s.coupling,
    macros: s.macros,
    mouthController: s.mouthController,
    rawLayer: s.rawLayer,
  }
}

/** useMemo, but the deps are values we own — kept tiny to avoid a hook import churn. */
function useDerived(fn, deps) {
  const ref = useRef({ deps: null, value: null })
  const same = ref.current.deps && ref.current.deps.length === deps.length
    && ref.current.deps.every((d, i) => d === deps[i])
  if (!same) ref.current = { deps, value: fn() }
  return ref.current.value
}

/** Fill percentage for the shared .aos-slider gradient track. */
function fillPct(value, lo, hi) {
  const span = hi - lo
  if (!(span > 0)) return '0%'
  return `${Math.max(0, Math.min(1, (value - lo) / span)) * 100}%`
}

const sectionLabel = {
  color: 'var(--m-fg-3)', fontSize: 10, textTransform: 'uppercase',
  letterSpacing: 1, padding: '6px 0 2px',
}
const ctrlLabel = {
  flex: 'none', width: 118, color: 'var(--m-fg-2)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const noteStyle = {
  padding: '6px 8px', borderRadius: 4, lineHeight: 1.45, marginBottom: 8,
  background: 'color-mix(in srgb, var(--m-warn) 16%, transparent)',
  color: 'var(--m-fg-2)',
}
const btnStyle = {
  font: 'inherit', fontSize: 10, cursor: 'pointer',
  padding: '2px 7px', borderRadius: 4,
  border: '1px solid var(--m-line)',
  background: 'var(--m-panel-hi)', color: 'var(--m-fg-2)',
}
